import { useMemo, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNetworkData } from '../../hooks/network/useNetworkData';
import { useQuery } from '@tanstack/react-query';
import SetupPageLayout from '../../components/setup/SetupPageLayout';
import RegionDefinitionWizard from '../../components/region/RegionDefinitionWizard';
import EditOrCreateModal from '../../components/setup/EditOrCreateModal';
import api from '../../API';
import {
    BLOCK_SETUP_ROUTE,
    DATA_MANAGER_HOME,
    DATA_MANAGER_WORKSPACE_LABEL,
    STAIN_SETUP_ROUTE,
    getDataManagerStep,
} from '../../constants/setupRoadmapSteps';
import styles from './RegionSetup.module.css';

const regionStep = getDataManagerStep('region');

function RegionSetup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { local, isLoading } = useNetworkData();
    
    const wizardType = searchParams.get('wizard'); // 'region' or null
    const editId = searchParams.get('editId'); // ID of item being edited, or null

    // Modal state for edit/create choice
    const [modalState, setModalState] = useState({
        isOpen: false,
        schemaKey: null,
        schemaTitle: null,
    });

    // About region definitions info box (hidden by default)
    const [showAboutInfo, setShowAboutInfo] = useState(false);

    // Fetch existing regions and stains (to check if stains exist)
    const { data: regionsResponse, refetch: refetchRegions } = useQuery({
        queryKey: ['bdsa-region'],
        queryFn: () => api.get('/bdsa/region/template'),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    const { data: stainsResponse } = useQuery({
        queryKey: ['bdsa-stain'],
        queryFn: () => api.get('/bdsa/stain'),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch region schema
    const { data: schemasResponse } = useQuery({
        queryKey: ['bdsa-schema', 'region'],
        queryFn: () => api.get('/bdsa/schema', { query: { name: 'region', includeDrafts: false } }),
        enabled: !isLoading && !!local,
        staleTime: 5 * 60 * 1000,
    });

    // Compute data arrays (safe to compute even if responses aren't ready)
    const regions = regionsResponse?.status === 200 && Array.isArray(regionsResponse.data)
        ? regionsResponse.data
        : [];

    const stains = stainsResponse?.status === 200 && Array.isArray(stainsResponse.data)
        ? stainsResponse.data
        : [];

    const hasStains = stains.length > 0;

    // Get latest schema and available regions
    const latestSchema = useMemo(() => {
        if (schemasResponse?.status === 200 && Array.isArray(schemasResponse.data) && schemasResponse.data.length > 0) {
            return schemasResponse.data.sort((a, b) => (b.version || 0) - (a.version || 0))[0];
        }
        return null;
    }, [schemasResponse]);

    const availableRegions = useMemo(() => {
        if (!latestSchema) return [];
        const regionProperties = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties || {};
        return Object.entries(regionProperties).map(([key, value]) => ({
            key,
            title: value.title || key,
            abbreviation: value.abbreviation || '',
            regionCategory: value.regionCategory || '',
            recommended: value.recommended === true,
        }));
    }, [latestSchema]);

    // Group regions by recommended status
    const recommendedRegions = useMemo(() => {
        return availableRegions.filter(region => region.recommended);
    }, [availableRegions]);

    const additionalRegions = useMemo(() => {
        return availableRegions.filter(region => !region.recommended);
    }, [availableRegions]);

    // Helper function to count definitions for a schema key
    const getDefinitionCountForSchemaKey = useCallback((schemaKey) => {
        if (!regions.length) return 0;
        return regions.filter(region => region.schemaRegionKey === schemaKey).length;
    }, [regions]);

    // Helper function to get definitions for a schema key
    const getDefinitionsForSchemaKey = useCallback((schemaKey) => {
        if (!regions.length) return [];
        return regions.filter(region => region.schemaRegionKey === schemaKey);
    }, [regions]);

    // Stable navigation functions - must be before any early returns
    const openWizard = useCallback((itemId = null) => {
        const params = new URLSearchParams();
        params.set('wizard', 'region');
        params.set('wizardStep', '1');
        if (itemId) {
            params.set('editId', itemId);
        }
        navigate(`/manage/region?${params.toString()}`);
    }, [navigate]);

    const openWizardWithSchemaKey = useCallback((schemaKey) => {
        const params = new URLSearchParams();
        params.set('wizard', 'region');
        params.set('wizardStep', '2');
        params.set('schemaKey', schemaKey);
        navigate(`/manage/region?${params.toString()}`);
    }, [navigate]);

    // Handle schema pill click - show modal if definitions exist, otherwise create new
    const handleSchemaPillClick = useCallback((schemaKey, schemaTitle) => {
        const existingDefinitions = getDefinitionsForSchemaKey(schemaKey);
        if (existingDefinitions.length > 0) {
            setModalState({
                isOpen: true,
                schemaKey,
                schemaTitle,
            });
        } else {
            openWizardWithSchemaKey(schemaKey);
        }
    }, [getDefinitionsForSchemaKey, openWizardWithSchemaKey]);

    const closeWizard = useCallback(() => {
        navigate('/manage/region');
    }, [navigate]);

    // Determine editing item from URL - must be before any early returns
    const editingItemFromUrl = useMemo(() => {
        if (!wizardType || wizardType !== 'region' || !editId) return null;
        return regions.find(r => r._id === editId);
    }, [wizardType, editId, regions]);

    // Redirect to initial config if not configured
    if (!isLoading && !local) {
        return <div><h2>Server not configured</h2><p>Ask an administrator to complete initial server setup.</p></div>;
    }

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const hasRegions = regions.length > 0;

    const handleRegionWizardComplete = () => {
        closeWizard();
        refetchRegions();
    };

    // If wizard is open, show it
    if (wizardType === 'region') {
        return (
            <RegionDefinitionWizard
                initialItem={editingItemFromUrl}
                onComplete={handleRegionWizardComplete}
                onCancel={closeWizard}
            />
        );
    }

    return (
        <SetupPageLayout
            stepNumber={regionStep.number}
            totalSteps={regionStep.total}
            title={regionStep.title}
            description={
                <>
                    <p className={styles.infoSummaryText}>
                        Pre-define conventional options (sectioning orientation, landmarks) for schema regions. These region definitions will be mapped to block labels in block definitions, protocols, and case creation.{' '}
                        <button
                            type="button"
                            className={styles.moreButton}
                            onClick={() => setShowAboutInfo(prev => !prev)}
                            aria-expanded={showAboutInfo}
                        >
                            {showAboutInfo ? 'Less' : 'More'}
                        </button>
                    </p>
                    {showAboutInfo && (
                        <div className={styles.infoSection}>
                            <h4>About Region Definitions</h4>
                            <p>Region definitions pre-configure conventional options for schema regions:</p>
                            <ul>
                                <li><strong>Sectioning orientation</strong>: Hemisphere and slice orientation</li>
                                <li><strong>Landmarks</strong>: Anatomical landmarks present in the region</li>
                                <li><strong>Damage</strong>: Types of damage present (optional)</li>
                            </ul>
                            <p>These definitions are mapped to block labels in block definitions (next step), protocols (future), and during case creation to automatically add accurate metadata to slides based on block assignments.</p>
                        </div>
                    )}
                </>
            }
            backLink={DATA_MANAGER_HOME}
            backLinkText={DATA_MANAGER_WORKSPACE_LABEL}
            onNext={hasRegions ? () => navigate(BLOCK_SETUP_ROUTE) : undefined}
            onPrevious={() => navigate(STAIN_SETUP_ROUTE)}
            nextDisabled={!hasRegions}
            nextLabel={hasRegions ? "Next: Sectioning protocols" : "Create at least one region to continue"}
            previousLabel="Stain Definitions"
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3>Region Definitions</h3>
                </div>
                {!hasStains ? (
                    <div className={styles.emptyState}>
                        <p>No stain definitions found.</p>
                        <p>You need to create at least one stain definition before creating regions.</p>
                        <button
                            onClick={() => navigate(STAIN_SETUP_ROUTE)}
                            className={styles.createButton}
                        >
                            Go to Stain Definitions
                        </button>
                    </div>
                ) : regions.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No region definitions found.</p>
                        <p>Create at least one region definition to continue.</p>
                        <button
                            onClick={() => openWizard()}
                            className={styles.createButton}
                        >
                            Create First Region
                        </button>
                    </div>
                ) : (
                    <div className={styles.list}>
                        <p className={styles.count}>{regions.length} region{regions.length !== 1 ? 's' : ''} created</p>
                        <div className={styles.itemsList}>
                            {regions.map(region => (
                                <div 
                                    key={region._id} 
                                    className={styles.item}
                                    onClick={() => openWizard(region._id)}
                                    title="Click to edit"
                                >
                                    <span className={styles.itemName}>{region.displayName || region.abbreviation}</span>
                                    <span className={styles.abbreviation}>({region.abbreviation})</span>
                                </div>
                            ))}
                            <div 
                                className={`${styles.item} ${styles.addItem}`}
                                onClick={() => openWizard()}
                                title="Click to create a new region definition"
                            >
                                + Add New Region
                            </div>
                        </div>
                    </div>
                )}

                {/* Schema Options Section */}
                {latestSchema && availableRegions.length > 0 && (
                    <div className={styles.schemaOptions}>
                        <h4>Available Schema Options</h4>
                        
                        {/* Recommended Section */}
                        {recommendedRegions.length > 0 && (
                            <div className={styles.recommendedSection}>
                                <h5 className={styles.sectionHeader}>Recommended</h5>
                                <div className={styles.schemaPillsList}>
                                    {recommendedRegions.map(region => {
                                        const count = getDefinitionCountForSchemaKey(region.key);
                                        return (
                                            <div
                                                key={region.key}
                                                className={styles.schemaPill}
                                                onClick={() => handleSchemaPillClick(region.key, region.title)}
                                                title={`Click to ${count > 0 ? 'edit existing or create a new' : 'create a'} definition for ${region.title}`}
                                            >
                                                <div className={styles.schemaPillHeader}>
                                                    <span className={styles.schemaPillName}>{region.title}</span>
                                                    {region.abbreviation && (
                                                        <span className={styles.schemaPillAbbr}>({region.abbreviation})</span>
                                                    )}
                                                    {region.recommended && (
                                                        <span className={styles.recommendedBadge}>Recommended</span>
                                                    )}
                                                    {count > 0 && (
                                                        <span className={styles.schemaPillCount}>
                                                            <span className={styles.checkmark}>✓</span> {count}
                                                        </span>
                                                    )}
                                                </div>
                                                {region.regionCategory && (
                                                    <span className={styles.schemaPillType}>{region.regionCategory}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Additional Section */}
                        {additionalRegions.length > 0 && (
                            <div className={styles.additionalSection}>
                                <h5 className={styles.sectionHeader}>Additional</h5>
                                <div className={styles.schemaPillsList}>
                                    {additionalRegions.map(region => {
                                        const count = getDefinitionCountForSchemaKey(region.key);
                                        return (
                                            <div
                                                key={region.key}
                                                className={styles.schemaPill}
                                                onClick={() => handleSchemaPillClick(region.key, region.title)}
                                                title={`Click to ${count > 0 ? 'edit existing or create a new' : 'create a'} definition for ${region.title}`}
                                            >
                                                <div className={styles.schemaPillHeader}>
                                                    <span className={styles.schemaPillName}>{region.title}</span>
                                                    {region.abbreviation && (
                                                        <span className={styles.schemaPillAbbr}>({region.abbreviation})</span>
                                                    )}
                                                    {count > 0 && (
                                                        <span className={styles.schemaPillCount}>
                                                            <span className={styles.checkmark}>✓</span> {count}
                                                        </span>
                                                    )}
                                                </div>
                                                {region.regionCategory && (
                                                    <span className={styles.schemaPillType}>{region.regionCategory}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Edit/Create Modal */}
                <EditOrCreateModal
                    isOpen={modalState.isOpen}
                    onClose={() => setModalState({ isOpen: false, schemaKey: null, schemaTitle: null })}
                    type="region"
                    schemaTitle={modalState.schemaTitle || ''}
                    existingDefinitions={modalState.schemaKey ? getDefinitionsForSchemaKey(modalState.schemaKey) : []}
                    onEdit={(definitionId) => {
                        openWizard(definitionId);
                    }}
                    onCreate={() => {
                        if (modalState.schemaKey) {
                            openWizardWithSchemaKey(modalState.schemaKey);
                        }
                    }}
                />
            </div>
        </SetupPageLayout>
    );
}

export default RegionSetup;
